import React, { useState, useEffect, useCallback } from "react"
import { DataGrid } from "@mui/x-data-grid"
import Alert from "@mui/material/Alert"

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Formats a flat list of property_owner join rows (already containing
 * nested person / company objects) into a displayable owners string.
 */
function formatOwners(propertyOwners = []) {
    return propertyOwners
        .map((po) => {
            if (po.person) {
                const initial = po.person.first_name
                    ? po.person.first_name[0] + ". "
                    : ""
                return initial + po.person.last_name
            }
            if (po.company) return po.company.name
            return "?"
        })
        .join(", ")
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const COLUMNS = [
    { field: "short_name", headerName: "Nom court",     flex: 1 },
    { field: "kind",       headerName: "Type de bien",  flex: 2 },
    { field: "city",       headerName: "Ville",         flex: 2 },
    { field: "owners",     headerName: "Propriétaires", flex: 3 },
]

/**
 * Renders the property directory as a DataGrid.
 *
 * Data responsibilities:
 *  - Initial fetch on mount via Supabase (nested select to get owners in one
 *    round-trip instead of N+1 queries).
 *  - Real-time updates via Supabase Realtime subscriptions on both `property`
 *    and `property_owner` tables — a single channel is enough because Supabase
 *    allows multiple `on()` listeners on the same channel.
 *
 * The selection model is *controlled* (rowSelectionModel prop) so that an
 * external deselection (e.g. after a delete in the form) is reflected here
 * without extra state synchronisation.
 */
export default function PropertiesTable({ supabase, selectedPropertyId, setSelectedPropertyId }) {
    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    // -------------------------------------------------------------------------
    // Data fetching
    // -------------------------------------------------------------------------

    const fetchRows = useCallback(async () => {
        const { data, error: fetchError } = await supabase
            .from("property")
            .select(`
                id,
                short_name,
                kind,
                city,
                property_owner (
                    owner_person_id,
                    owner_company_id,
                    person:owner_person_id ( first_name, last_name ),
                    company:owner_company_id ( name )
                )
            `)
            .order("short_name")

        if (fetchError) {
            console.error("PropertiesTable – fetch error:", fetchError)
            setError("Impossible de charger les biens.")
            setLoading(false)
            return
        }

        setRows(
            (data ?? []).map((p) => ({
                id: p.id,
                short_name: p.short_name,
                kind: p.kind,
                city: p.city,
                owners: formatOwners(p.property_owner),
            }))
        )
        setLoading(false)
    }, [supabase])

    // -------------------------------------------------------------------------
    // Realtime subscription
    // -------------------------------------------------------------------------

    useEffect(() => {
        fetchRows()

        // A single channel can listen to multiple tables.
        // We refetch entirely rather than patching rows in-place: this keeps
        // the aggregation logic (formatOwners) in one place and avoids
        // subtle inconsistencies when property_owner rows change.
        const channel = supabase
            .channel("properties_table_realtime")
            .on(
                "postgres_changes",
                { event: "*", schema: "public", table: "property" },
                fetchRows
            )
            .on(
                "postgres_changes",
                { event: "*", schema: "public", table: "property_owner" },
                fetchRows
            )
            .subscribe()

        return () => {
            supabase.removeChannel(channel)
        }
    }, [fetchRows])

    // -------------------------------------------------------------------------
    // Render
    // -------------------------------------------------------------------------

    if (error) {
        return <Alert severity="error">{error}</Alert>
    }

    return (
        <DataGrid
            autoHeight
            checkboxSelection
            disableMultipleRowSelection
            loading={loading}
            rows={rows}
            columns={COLUMNS}
            // Controlled selection — keeps table in sync when the form clears
            // selectedPropertyId (e.g. after a successful delete).
            rowSelectionModel={selectedPropertyId ? [selectedPropertyId] : []}
            onRowSelectionModelChange={(model) => {
                setSelectedPropertyId(model.length ? model[0] : null)
            }}
            getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
            }
            initialState={{
                pagination: { paginationModel: { pageSize: 20 } },
            }}
            pageSizeOptions={[10, 20, 50]}
            density="compact"
        />
    )
}